/* eslint-disable */
import {
  FC,
  useState,
  useEffect,
  useRef,
  createContext,
  useContext,
  Dispatch,
  SetStateAction,
} from "react";
import { LayoutSplashScreen } from "../../../../_stream/layout/core";
import { AuthModel, UserModel } from "./_models";
import * as authHelper from "./AuthHelpers";
import { WithChildren } from "../../../../_stream/helpers";
import { jwtDecode } from "jwt-decode";
import {
  getConversionRates,
  getPermissions,
  switchBranch,
  switchOrganization,
  acceptInviteAsExistingUser,
} from "./_requests";
import { isRefreshBackoffActive, performTokenRefresh } from "./tokenRefresh";
import { RootContext } from "../../../../store";
import dayjs from "dayjs";
import {
  trackMerchant,
  MerchantEvents,
  reset,
} from "../../../../_stream/helpers/analytics";

type AuthContextProps = {
  auth: AuthModel | undefined;
  saveAuth: (auth: AuthModel | undefined) => void;
  currentUser: UserModel | undefined;
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>;
  deepLink?: string;
  registerDeepLink: (link: string) => void;
  removeDeepLink: () => void;
  logout: () => void;
  updateToken: () => Promise<void>;
  switchUserBranch: (branchId: string | null) => void;
  switchUserOrganization: (organizationId: string) => Promise<boolean>;
  acceptOrganizationInvite: (
    inviteId: string,
    recaptchaToken: string
  ) => Promise<{ success: true } | { success: false; status?: number }>;
};

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
  deepLink: undefined,
  registerDeepLink: () => null,
  removeDeepLink: () => null,
  updateToken: async () => {},
  switchUserBranch: () => {},
  switchUserOrganization: async () => false,
  acceptOrganizationInvite: async () => ({ success: false }),
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => {
  return useContext(AuthContext);
};

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth());
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>();
  const [deepLink, setDeepLink] = useState<string | undefined>(undefined);
  const { permission: permissionStore } = useContext(RootContext);

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth);
    if (auth) {
      authHelper.setAuth(auth);
    } else {
      authHelper.removeAuth();
    }
  };

  const registerDeepLink = (link: string) => {
    if (link.startsWith("/auth")) return;
    setDeepLink(link);
  };

  const removeDeepLink = () => {
    setDeepLink(undefined);
  };

  const logout = () => {
    trackMerchant(MerchantEvents.LOGOUT);
    reset();
    saveAuth(undefined);
    setCurrentUser(undefined);
    permissionStore.setPermissions([]);
    permissionStore.permissionsLoaded = false;
    permissionStore.setPermissionsLoaded(false);

    if (deepLink) {
      setDeepLink(undefined);
    }
  };

  const updateToken = async () => {
    if (isRefreshBackoffActive()) {
      throw new Error("Token refresh backoff active");
    }

    try {
      const auth = await performTokenRefresh();
      saveAuth(auth);

      if (auth.access_token) {
        const decoded = jwtDecode(auth.access_token);
        setCurrentUser(decoded as UserModel);
      }
    } catch (error) {
      console.error("Failed to refresh token", error);
      throw error;
    }
  };

  const switchUserBranch = async (branchId: string | null) => {
    try {
      const response = await switchBranch(branchId);
      const expiryDate = dayjs()
        .add(response.data.expires_in || 0, "seconds")
        .format("YYYY-MM-DD HH:mm:ss");
      const access_token = response.data.access_token;

      localStorage.setItem("ZD-launcherLabelRemoved", true.toString());

      localStorage.setItem("access_token", access_token);
      localStorage.setItem("access_token_expiry", expiryDate);
      saveAuth(response.data);

      if (access_token) {
        const decoded = jwtDecode(access_token);
        setCurrentUser(decoded as UserModel);
      }
    } catch (e) {
      console.error("Error while updating branch", e);
    }
  };

  const applyAuthResponse = (auth: AuthModel) => {
    const access_token = auth?.access_token;

    // A response missing access_token must never reach localStorage — it
    // would coerce to the literal string "undefined" (setItem stringifies),
    // which reads back as truthy on the next load, fails jwtDecode, and logs
    // the user out. Bail out loudly instead of silently corrupting the
    // session.
    if (!access_token) {
      console.error(
        "[Auth] applyAuthResponse called with no access_token — aborting",
        auth
      );
      return false;
    }

    const expiryDate = dayjs()
      .add(auth.expires_in || 0, "seconds")
      .format("YYYY-MM-DD HH:mm:ss");

    console.log("[Auth] applyAuthResponse", {
      tokenLength: access_token.length,
      expiryDate,
    });

    localStorage.setItem("ZD-launcherLabelRemoved", true.toString());

    localStorage.setItem("access_token", access_token);
    localStorage.setItem("access_token_expiry", expiryDate);
    saveAuth(auth);

    const decoded = jwtDecode(access_token);
    console.log("[Auth] decoded token", decoded);
    setCurrentUser(decoded as UserModel);
    return true;
  };

  const switchUserOrganization = async (organizationId: string) => {
    try {
      const response = await switchOrganization(organizationId);
      return applyAuthResponse(response.data);
    } catch (e) {
      console.error("Error while switching organization", e);
      return false;
    }
  };

  const acceptOrganizationInvite = async (
    inviteId: string,
    recaptchaToken: string
  ) => {
    try {
      console.log("[Auth] acceptOrganizationInvite calling API", {
        inviteId,
      });
      const response = await acceptInviteAsExistingUser({
        invite_id: inviteId,
        "g-recaptcha-response": recaptchaToken,
      });
      console.log("[Auth] acceptOrganizationInvite response", response.data);

      if (!applyAuthResponse(response.data)) {
        return { success: false as const, status: undefined };
      }
      return { success: true as const };
    } catch (e: any) {
      console.error("Error while accepting organization invite", e);
      return { success: false as const, status: e?.response?.status };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        auth,
        saveAuth,
        currentUser,
        setCurrentUser,
        logout,
        deepLink,
        registerDeepLink,
        removeDeepLink,
        updateToken,
        switchUserBranch,
        switchUserOrganization,
        acceptOrganizationInvite,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const AuthInit: FC<WithChildren> = ({ children }) => {
  const { auth, currentUser, logout, setCurrentUser, updateToken } = useAuth();
  const [showSplashScreen, setShowSplashScreen] = useState(true);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [tokenRefreshed, setTokenRefreshed] = useState(false);
  const access_token = localStorage.getItem("access_token");
  const { permission: permissionStore, conversionRates: conversionRatesStore } =
    useContext(RootContext);

  useEffect(() => {
    const requestUser = async () => {
      try {
        if (access_token && !tokenRefreshed) {
          const tokenExpiryStr = localStorage.getItem("access_token_expiry");

          if (tokenExpiryStr) {
            const tokenExpiry = dayjs(tokenExpiryStr);
            const secondsUntilExpiry = tokenExpiry.diff(dayjs(), "seconds");

            if (secondsUntilExpiry <= 300) {
              console.log(
                "Token expired or expiring soon, refreshing proactively on app init"
              );
              try {
                setTokenRefreshed(true);
                await updateToken();
                console.log("Token refreshed successfully on app init");
              } catch (refreshError) {
                console.error(
                  "Failed to refresh token on app init",
                  refreshError
                );
                setTokenRefreshed(false);
              }
            }
          }
        }

        if (currentUser || access_token) {
          if (!permissionStore.isPermissionsLoaded()) {
            if (
              window.location.pathname.search("/invoice-consent") >= 0 ||
              window.location.pathname.startsWith("/payment-link/")
            ) {
              return;
            }
          }
        }

        if (currentUser) {
          return currentUser;
        }
        if (access_token) {
          const decoded = jwtDecode(access_token);
          setCurrentUser(decoded as UserModel);
          return;
        }
      } catch (error) {
        console.error(error);
        if (currentUser) {
          logout();
        }
      } finally {
        setPermissionsLoaded(true);
        setShowSplashScreen(false);
      }
    };

    requestUser();
  }, []);

  const conversionRatesFetchingRef = useRef(false);

  useEffect(() => {
    if (
      auth?.access_token &&
      !conversionRatesStore.isLoaded &&
      !conversionRatesFetchingRef.current
    ) {
      conversionRatesFetchingRef.current = true;
      getConversionRates()
        .then((response) => {
          conversionRatesStore.setRates(response.data?.conversion_rates ?? {});
        })
        .catch((error) => {
          console.error("Failed to fetch conversion rates", error);
          conversionRatesFetchingRef.current = false;
        });
    }
  }, [auth?.access_token]);

  if (showSplashScreen || !permissionsLoaded) {
    return <LayoutSplashScreen />;
  }

  return <>{children}</>;
};

export { AuthProvider, AuthInit, useAuth };

/* eslint-enable */
